import { useEffect, useState } from 'react';
import type { FC } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useRequest } from 'loti-request';

import { useTitle } from 'app/hooks';
import LoginLayout from 'app/components/LoginLayout';
import LoginContent from 'app/components/LoginContent';
import { LOGIN_PATH } from 'app/constants/routes';
import { getRegistrationUrl } from 'app/utils/api';
import Button from 'app/components/Button';
import Loader from 'app/components/Loader';
import style from 'app/pages/SignupPage/style.scss';

import type { ResetPasswordData, ResetPasswordDataError, ResetPasswordFormValues } from './types';
import { isPendingResetPassword, resetPasswordInitialValues, resetPasswordValidate } from './utils';
import ResetPasswordView from './views/resetpassword';

const ResetPasswordPage: FC = () => {
  const [t] = useTranslation();

  useTitle('Reset password | PandaScore');

  const navigate = useNavigate();
  const [params] = useSearchParams();
  const reset_token = params.get('reset_token');
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  const [forgotPasswordState, setForgotPasswordState] = useState<'success' | 'error' | null>(null);

  const {
    fetch,
    data: resetPasswordData,
    error
  } = useRequest<ResetPasswordData>({
    onError: () => {
      setIsFirstLoad(false);
    },
    onSuccess: () => {
      setIsFirstLoad(false);
    }
  });

  const { fetch: requestNewLink } = useRequest({
    url: getRegistrationUrl('/bookmakers/reset_password/request'),
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    onSuccess: () => {
      setForgotPasswordState('success');
    },
    onError: () => {
      setForgotPasswordState('error');
    }
  });

  useEffect(() => {
    if (!reset_token) {
      navigate(LOGIN_PATH);
    } else {
      fetch({
        url: getRegistrationUrl(`/bookmakers/reset_password/request/${reset_token}`),
        method: 'GET'
      });
    }
  }, [reset_token]);

  useEffect(() => {
    if (resetPasswordData && !isPendingResetPassword(resetPasswordData)) {
      setResetPasswordSuccess(true);
    }
  }, [resetPasswordData]);

  const submit = ({ password, password_confirmation }: ResetPasswordFormValues) => {
    fetch({
      url: getRegistrationUrl(`/bookmakers/reset_password/confirm/${reset_token}`),
      method: 'PATCH',
      body: {
        password,
        password_confirmation
      },
      headers: {
        'Content-Type': 'application/json'
      }
    });
  };

  const resetPasswordError = error ? (error as ResetPasswordDataError) : undefined;
  const hasError =
    resetPasswordError &&
    ['no_reset_password_found', 'reset_password_expired'].includes(resetPasswordError.message);

  return (
    <LoginLayout title={hasError ? undefined : t('reset_password-title')}>
      {resetPasswordSuccess ? (
        <LoginContent
          icon="done"
          title={t('reset_password-success-title')}
          action={
            <Button onClick={() => navigate(LOGIN_PATH)} className={style.contentActionButton}>
              {t('reset_password-success-action')}
            </Button>
          }
        />
      ) : (
        <>
          {(() => {
            if (forgotPasswordState === 'success') {
              return (
                <LoginContent
                  icon="done"
                  title={t('forgot_password-success-title')}
                  subtitle={t('forgot_password-success-content')}
                />
              );
            }
            if (
              (resetPasswordError &&
                (resetPasswordError.message === 'no_reset_password_found' ||
                  (resetPasswordError.message === 'reset_password_expired' &&
                    !resetPasswordError.email))) ||
              forgotPasswordState === 'error'
            ) {
              return (
                <LoginContent
                  title={t('reset_password-not_found-title')}
                  subtitle={t('reset_password-not_found-subtitle')}
                  icon="wait"
                  action={
                    <Button
                      onClick={() => navigate(LOGIN_PATH)}
                      className={style.contentActionButton}
                    >
                      {t('reset_password-not_found-action')}
                    </Button>
                  }
                >
                  <div className={style.content}>
                    <p className={style.title1}>{t('reset_password-not_found-content-title')}</p>
                    <p className={style.text}>{t('reset_password-not_found-content-text')}</p>
                  </div>
                </LoginContent>
              );
            }
            if (
              resetPasswordError &&
              resetPasswordError.message === 'reset_password_expired' &&
              resetPasswordError.email
            ) {
              return (
                <LoginContent
                  title={t('reset_password-expired-title')}
                  subtitle={t('reset_password-expired-subtitle')}
                  icon="wait"
                  action={
                    <Button
                      onClick={() => {
                        requestNewLink({ body: { email: resetPasswordError.email } });
                      }}
                      className={style.contentActionButton}
                    >
                      {t('reset_password-expired-action')}
                    </Button>
                  }
                >
                  <div className={style.content}>
                    <p className={style.title1}>{t('reset_password-expired-content-title')}</p>
                    <p className={style.text}>{t('reset_password-expired-content-text')}</p>
                  </div>
                </LoginContent>
              );
            }

            if (isFirstLoad) {
              return (
                <div className={style.loaderContainer}>
                  <Loader />
                </div>
              );
            }
            const failed = !!resetPasswordError;

            return (
              <Formik<ResetPasswordFormValues>
                initialValues={resetPasswordInitialValues}
                onSubmit={submit}
                validate={(values: ResetPasswordFormValues) => resetPasswordValidate(values, t)}
              >
                {({ isValid }) => <ResetPasswordView isValid={isValid} failed={failed} />}
              </Formik>
            );
          })()}
        </>
      )}
    </LoginLayout>
  );
};

ResetPasswordPage.displayName = 'ResetPasswordPage';

export default ResetPasswordPage;
